import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Check,
  LayoutGrid, Workflow, LineChart, Sparkles,
  Cloud, Network, ShieldCheck, Boxes,
  Target, ClipboardCheck, Globe, Activity,
  Search, Layers, TrendingUp, BarChart3,
  Lightbulb, Database, Rocket, Home,
} from "lucide-react";
import { useServiceDetails, serviceOrder } from "../data/serviceDetails";
import { useSiteContent } from "../data/content";
import SeoHead, { SEO_CONFIG } from "../seo/SeoHead"; // NEU
import { useLanguage } from "../context/LanguageContext"; // NEU

const ICONS = {
  LayoutGrid, Workflow, LineChart, Sparkles,
  Cloud, Network, ShieldCheck, Boxes,
  Target, ClipboardCheck, Globe, Activity,
  Search, Layers, TrendingUp, BarChart3,
  Lightbulb, Database, Rocket,
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const details = useServiceDetails();
  const content = useSiteContent();
  const ui = content.ui.serviceDetail;
  const data = details[slug];
  const { lang } = useLanguage(); // SEO: aktuelle Sprache
  const seoService = SEO_CONFIG.services[slug];
  const seo = seoService ? (seoService[lang] || seoService.de) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-[#0B1F3A]">{ui.notFoundTitle}</h1>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-teal-700 hover:text-teal-800">
            <ArrowLeft className="w-4 h-4" /> {ui.notFoundLink}
          </Link>
        </div>
      </div>
    );
  }

  // SEO: Dynamischer Title/Meta pro Leistungsseite
  const seoTitle = seo?.title || data.title;
  const seoDesc = seo?.description || data.subtitle;
  const seoCanonical = seo?.canonical || `https://liesegang-consulting.com/leistungen/${slug}`;

  const HeroIcon = ICONS[data.icon] || LayoutGrid;
  const currentIdx = serviceOrder.indexOf(slug);
  const next = serviceOrder[(currentIdx + 1) % serviceOrder.length];
  const nextData = details[next];

  return (
    <div className="bg-white text-slate-900">
      {/* SEO: dynamischer Title & Meta-Description pro Unterseite */}
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        canonical={seoCanonical}
        lang={lang}
      />
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-50 via-white to-white" />
        <div className="absolute -z-10 top-0 right-0 w-[700px] h-[700px] bg-teal-100/40 blur-[120px] rounded-full" />
        <div className="absolute -z-10 top-40 -left-32 w-[500px] h-[500px] bg-blue-100/50 blur-[120px] rounded-full" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <nav className="flex items-center gap-2 text-sm text-slate-500 mb-10">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-[#0B1F3A]">
              <Home className="w-3.5 h-3.5" /> {ui.breadcrumbHome}
            </Link>
            <span>/</span>
            <button onClick={() => navigate("/#leistungen")} className="hover:text-[#0B1F3A]">
              {ui.breadcrumbServices}
            </button>
            <span>/</span>
            <span className="text-[#0B1F3A] font-medium">{data.title}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl bg-[#0B1F3A] text-white flex items-center justify-center shadow-lg shadow-slate-900/10">
                  <HeroIcon className="w-6 h-6" />
                </span>
                <span className="text-[12px] font-medium tracking-[0.18em] uppercase text-teal-700 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-full">
                  {data.eyebrow}
                </span>
              </div>
              <h1 className="mt-7 text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.05] font-semibold tracking-[-0.03em] text-[#0B1F3A]">
                {data.title}
              </h1>
              <p className="mt-6 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
                {data.subtitle}
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/#kontakt" className="group inline-flex items-center gap-2 bg-[#0B1F3A] hover:bg-[#102a4d] text-white px-6 py-3.5 rounded-lg text-[15px] font-medium transition-colors duration-200">
                  {ui.primaryCta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/" className="inline-flex items-center gap-2 text-[#0B1F3A] hover:text-teal-700 px-2 py-3.5 text-[15px] font-medium border-b border-slate-300 hover:border-teal-500 transition-colors duration-200">
                  <ArrowLeft className="w-4 h-4" /> {ui.back}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-2xl shadow-slate-900/10">
                <img src={data.image} alt={data.title} className="w-full h-[440px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1F3A]/40 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">{ui.overview}</span>
          <p className="mt-5 text-xl lg:text-2xl text-slate-700 leading-[1.55] tracking-[-0.005em]">{data.intro}</p>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">{ui.pillarsEyebrow}</span>
            <h2 className="mt-4 text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-[#0B1F3A] leading-[1.15]">{ui.pillarsTitle}</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {data.pillars.map((p, i) => {
              const Icon = ICONS[p.icon] || LayoutGrid;
              return (
                <div key={p.title} className="bg-white rounded-2xl p-7 border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 transition-[box-shadow,border-color] duration-300">
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-mono text-slate-400">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-[#0B1F3A]">{p.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">{ui.approachEyebrow}</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-[#0B1F3A] leading-[1.15]">
                {ui.approachTitleLine1}<br />
                <span className="text-slate-500">{ui.approachTitleLine2}</span>
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">{ui.approachLead}</p>
            </div>
            <div className="lg:col-span-8">
              <ol className="relative border-l border-slate-200 ml-2">
                {data.approach.map((s) => (
                  <li key={s.step} className="ml-6 pb-8 last:pb-0">
                    <span className="absolute -left-[13px] mt-1 w-6 h-6 rounded-full bg-[#0B1F3A] text-white text-[11px] font-semibold flex items-center justify-center ring-4 ring-white">
                      {s.step}
                    </span>
                    <h3 className="text-lg font-semibold text-[#0B1F3A]">{s.title}</h3>
                    <p className="mt-1 text-slate-600 leading-relaxed">{s.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-10">
            <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">{ui.deliverablesEyebrow}</span>
            <h3 className="mt-3 text-2xl lg:text-3xl font-semibold tracking-[-0.02em] text-[#0B1F3A]">{ui.deliverablesTitle}</h3>
            <ul className="mt-7 space-y-3">
              {data.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <span className="mt-0.5 w-5 h-5 rounded-md bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#0B1F3A] rounded-2xl p-8 lg:p-10 text-white relative overflow-hidden">
            <div className="absolute -top-32 -right-20 w-[400px] h-[400px] bg-teal-500/20 blur-[120px] rounded-full" />
            <span className="relative text-[12px] font-medium tracking-[0.2em] uppercase text-teal-300">{ui.techEyebrow}</span>
            <h3 className="relative mt-3 text-2xl lg:text-3xl font-semibold tracking-[-0.02em]">{ui.techTitle}</h3>
            <div className="relative mt-7 flex flex-wrap gap-2.5">
              {data.tech.map((t) => (
                <span key={t} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-slate-100 backdrop-blur-sm">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">{ui.useCasesEyebrow}</span>
            <h2 className="mt-4 text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-[#0B1F3A] leading-[1.15]">{ui.useCasesTitle}</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {data.useCases.map((u, i) => (
              <article key={u.title} className="group rounded-2xl border border-slate-200 p-7 hover:bg-[#0B1F3A] hover:border-[#0B1F3A] transition-colors duration-300">
                <div className="text-xs font-mono text-slate-400 group-hover:text-teal-300 transition-colors">
                  {ui.caseLabel} 0{i + 1}
                </div>
                <h3 className="mt-3 text-xl font-semibold text-[#0B1F3A] group-hover:text-white transition-colors">{u.title}</h3>
                <p className="mt-2 text-slate-600 group-hover:text-slate-300 leading-relaxed transition-colors">{u.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-8 lg:p-10">
            <h3 className="text-2xl lg:text-3xl font-semibold tracking-[-0.02em] text-[#0B1F3A] leading-[1.2]">{ui.ctaTitle}</h3>
            <p className="mt-4 text-slate-600 leading-relaxed">{ui.ctaDesc}</p>
            <Link to="/#kontakt" className="mt-6 inline-flex items-center gap-2 bg-[#0B1F3A] hover:bg-[#102a4d] text-white px-5 py-3 rounded-lg text-sm font-medium transition-colors">
              {ui.ctaButton} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <Link to={`/leistungen/${nextData.slug}`} className="group rounded-2xl bg-[#0B1F3A] text-white p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -bottom-24 -right-20 w-[400px] h-[400px] bg-teal-500/20 blur-[120px] rounded-full" />
            <div className="relative">
              <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-300">{ui.nextEyebrow}</span>
              <h3 className="mt-3 text-2xl lg:text-3xl font-semibold tracking-[-0.02em]">{nextData.title}</h3>
              <p className="mt-3 text-slate-300 leading-relaxed line-clamp-2">{nextData.subtitle}</p>
            </div>
            <div className="relative mt-8 inline-flex items-center gap-2 text-sm font-medium">
              {ui.nextCta}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
